<template>
  <div>
    <ErrorModal 
      :show="showError"
      :message="errorMessage"
      title="Validation Error"
      @close="showError = false"
    />
    <div class="modal-overlay">
      <div class="address-modal">
        <div class="modal-header">
          <span>Address</span>
          <div class="modal-header-actions">
            <button class="icon-btn" @click="$emit('help')" title="Help">?</button>
            <button class="icon-btn" @click="$emit('cancel')" title="Close">✖</button>
          </div>
        </div>
        <div class="modal-body">
          <div class="form-group-row" v-for="(row, rowIndex) in groupedFields" :key="'row-' + rowIndex">
            <div class="form-group" v-for="field in row" :key="field.key">
              <label :for="field.key">
                {{ field.label }}
                <span v-if="isRequired(field.key)" class="required">*</span>
              </label>
              <input
                :id="field.key"
                v-model="address[field.key]"
                :placeholder="field.label"
                type="text"
                class="form-control"
              />
            </div>
          </div>
          <div class="btn-row">
            <button class="btn btn-primary" @click="submit">OK</button>
            <button class="btn btn-secondary" @click="$emit('cancel')">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ErrorModal from '@/components/ErrorModal.vue';

export default {
  components: {
    ErrorModal
  },
  name: 'AddressModal',
  props: {
    value: { type: String, default: '' }
  },
  emits: ['ok', 'cancel', 'help', 'error'],
  data() {
    return {
      showError: false,
      errorMessage: '',
      address: {
        address1: '',
        address2: '',
        city: '',
        state: '',
        postalCode: '',
        country: ''
      },
      fields: [
        { key: 'address1', label: 'Address1' },
        { key: 'address2', label: 'Address2' },
        { key: 'city', label: 'City' },
        { key: 'state', label: 'State' },
        { key: 'postalCode', label: 'Postal code' },
        { key: 'country', label: 'Country' }
      ]
    };
  },
  mounted() {
    // Parse value into fields if provided
    if (this.value) {
      const lines = this.value.split('\n');
      this.fields.forEach((field, idx) => {
        this.address[field.key] = lines[idx] || '';
      });
    }
  },
  methods: {
    isRequired(fieldKey) {
      const requiredFields = ['address1', 'city', 'state', 'postalCode', 'country'];
      return requiredFields.includes(fieldKey);
    },
    validateForm() {
      // Check required fields: address1, city, state, postalCode, country
      const requiredFields = ['address1', 'city', 'state', 'postalCode', 'country'];
      const emptyFields = requiredFields.filter(field => !this.address[field].trim());
      
      if (emptyFields.length > 0) {
        // Convert field names to labels for better user feedback
        const emptyFieldLabels = emptyFields
          .map(field => this.fields.find(f => f.key === field).label)
          .join(', ');
        this.errorMessage = `Please fill in required fields: ${emptyFieldLabels}`;
        this.showError = true;
        return false;
      }
      return true;
    },
    submit() {
      if (!this.validateForm()) {
        return;
      }
      // Format address as a string (one per line) - always return 6 lines
      const lines = this.fields.map(f => this.address[f.key] || '');
      this.$emit('ok', lines.join('\n'));
    }
  },
  computed: {
    groupedFields() {
      // Group fields into pairs
      const groups = [];
      for (let i = 0; i < this.fields.length; i += 2) {
        groups.push(this.fields.slice(i, i + 2));
      }
      return groups;
    }
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.13);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
}
.address-modal {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 24px rgba(2, 136, 209, 0.10);
  width: 900px;
  max-width: 98vw;
  border: 1.5px solid #0099cc;
  padding: 0;
  display: flex;
  flex-direction: column;
}
.modal-header {
  background: #0099cc;
  color: #fff;
  font-weight: 700;
  font-size: 1.08rem;
  padding: 0.7rem 1.1rem;
  border-radius: 10px 10px 0 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.modal-header-actions {
  display: flex;
  gap: 0.3rem;
}
.icon-btn {
  background: none;
  border: none;
  color: #fff;
  font-size: 1.1rem;
  cursor: pointer;
  margin-left: 0.2rem;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  transition: background 0.18s;
}
.icon-btn:hover {
  background: #0288d1;
}
.modal-body {
  padding: 1.5rem 2.2rem 1.1rem 2.2rem;
}
.form-group-row {
  display: flex;
  flex-direction: row;
  gap: 1.5rem;
  margin-left: 0.5rem;
  margin-right: 0.5rem;
}
.form-group {
  flex: 1 1 0;
  margin-bottom: 0.1rem;
  display: flex;
  flex-direction: column;
}
.form-group label {
  font-weight: 600;
  margin-bottom: 0.1rem;
  color: #205080;
  font-size: 1.08rem;
  text-align: left;
  align-self: flex-start;
}
.form-control {
  border-radius: 7px;
  border: 1.5px solid #b6e2b6;
  padding: 0.7rem 1.1rem;
  font-size: 1.08rem;
  background: #fafdff;
  transition: border 0.2s, box-shadow 0.2s;
  outline: none;
  width: 100%;
  box-sizing: border-box;
}
.form-control:focus {
  border: 1.5px solid #0099cc;
  box-shadow: 0 0 0 2px #b6e2b6;
  background: #fff;
}
.btn-row {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.7em;
  margin-top: 0.5em;
  width: 100%;
  padding-right: 0.5rem;
}
.btn {
  background: #0099cc;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1.3rem;
  font-weight: 600;
  font-size: 1.02rem;
  cursor: pointer;
  transition: background 0.18s;
}
.btn.btn-primary {
  background: #0099cc;
}
.btn.btn-primary:hover {
  background: #0288d1;
}
.btn.btn-secondary {
  background: #e74c3c;
}
.btn.btn-secondary:hover {
  background: #c0392b;
}
.required {
  color: #e74c3c;
  margin-left: 2px;
}
</style> 